export const MAX_FANCY_TEXT_GRAPHEMES = 5_000
export const MAX_FANCY_TEXT_CODE_POINTS = 20_000

export const FANCY_TEXT_STYLE_IDS = [
  'bold',
  'italic',
  'bold-italic',
  'script',
  'fraktur',
  'double-struck',
  'sans-serif-bold',
  'monospace',
] as const

export type FancyTextStyle = (typeof FANCY_TEXT_STYLE_IDS)[number]

interface StyleMapping {
  upperStart: number
  lowerStart: number
  digitStart?: number
  exceptions?: Readonly<Record<string, number>>
}

const STYLE_MAPPINGS: Record<FancyTextStyle, StyleMapping> = {
  bold: { upperStart: 0x1d400, lowerStart: 0x1d41a, digitStart: 0x1d7ce },
  italic: {
    upperStart: 0x1d434,
    lowerStart: 0x1d44e,
    exceptions: { h: 0x210e },
  },
  'bold-italic': { upperStart: 0x1d468, lowerStart: 0x1d482 },
  script: {
    upperStart: 0x1d49c,
    lowerStart: 0x1d4b6,
    exceptions: {
      B: 0x212c,
      E: 0x2130,
      F: 0x2131,
      H: 0x210b,
      I: 0x2110,
      L: 0x2112,
      M: 0x2133,
      R: 0x211b,
      e: 0x212f,
      g: 0x210a,
      o: 0x2134,
    },
  },
  fraktur: {
    upperStart: 0x1d504,
    lowerStart: 0x1d51e,
    exceptions: { C: 0x212d, H: 0x210c, I: 0x2111, R: 0x211c, Z: 0x2128 },
  },
  'double-struck': {
    upperStart: 0x1d538,
    lowerStart: 0x1d552,
    digitStart: 0x1d7d8,
    exceptions: {
      C: 0x2102,
      H: 0x210d,
      N: 0x2115,
      P: 0x2119,
      Q: 0x211a,
      R: 0x211d,
      Z: 0x2124,
    },
  },
  'sans-serif-bold': { upperStart: 0x1d5d4, lowerStart: 0x1d5ee, digitStart: 0x1d7ec },
  monospace: { upperStart: 0x1d670, lowerStart: 0x1d68a, digitStart: 0x1d7f6 },
}

const UNSAFE_INVISIBLE_OR_CONTROL_PATTERN = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f\u00ad\u034f\u061c\u115f-\u1160\u17b4-\u17b5\u180b-\u180f\u200b-\u200c\u200e-\u200f\u202a-\u202e\u2060-\u206f\u3164\ufeff\uffa0\ufff9-\ufffb\u{1bca0}-\u{1bca3}\u{1d173}-\u{1d17a}\u{e0000}-\u{e007f}]/u
const MARK_OR_MODIFIER_PATTERN = /[\p{Mark}\p{Emoji_Modifier}]/u
const REGIONAL_INDICATOR_PATTERN = /\p{Regional_Indicator}/u
const EXTENDED_PICTOGRAPHIC_PATTERN = /\p{Extended_Pictographic}/u
const EMOJI_SEQUENCE_EXTENDER_PATTERN = /[\p{Mark}\p{Emoji_Modifier}\ufe0e\ufe0f]/u
const ZERO_WIDTH_JOINER = '\u200d'

interface SegmenterConstructor {
  new (locales?: string | string[], options?: { granularity: 'grapheme' }): {
    segment(text: string): Iterable<{ segment: string }>
  }
}

export interface FancyTextCounts {
  graphemes: number
  codePoints: number
  utf16Units: number
}

export type FancyTextValidation =
  | { status: 'success'; counts: FancyTextCounts; hasUnsafeControls: boolean }
  | { status: 'error'; error: 'grapheme_limit' | 'code_point_limit'; counts: FancyTextCounts }

function fallbackGraphemeSegments(text: string): string[] {
  const codePoints = [...text]
  const result: string[] = []

  for (let index = 0; index < codePoints.length; index += 1) {
    const codePoint = codePoints[index]!
    const previous = result.at(-1)
    const previousEndsWithJoiner = previous?.endsWith('\u200d') ?? false
    const isJoiner = codePoint === '\u200d'
    const isVariationSelector = /[\ufe00-\ufe0f]/u.test(codePoint)
    const shouldPairFlags = REGIONAL_INDICATOR_PATTERN.test(codePoint)
      && previous !== undefined
      && [...previous].every((part) => REGIONAL_INDICATOR_PATTERN.test(part))
      && [...previous].length % 2 === 1

    if (
      previous !== undefined
      && (MARK_OR_MODIFIER_PATTERN.test(codePoint)
        || isVariationSelector
        || isJoiner
        || previousEndsWithJoiner
        || shouldPairFlags)
    ) {
      result[result.length - 1] = previous + codePoint
    } else {
      result.push(codePoint)
    }
  }

  return result
}

export function segmentFancyText(text: string): string[] {
  const Segmenter = (Intl as typeof Intl & { Segmenter?: SegmenterConstructor }).Segmenter
  if (!Segmenter) return fallbackGraphemeSegments(text)
  return [...new Segmenter(undefined, { granularity: 'grapheme' }).segment(text)]
    .map(({ segment }) => segment)
}

function isEmojiZwjAt(codePoints: readonly string[], index: number): boolean {
  let previousIndex = index - 1
  while (
    previousIndex >= 0
    && EMOJI_SEQUENCE_EXTENDER_PATTERN.test(codePoints[previousIndex]!)
  ) {
    previousIndex -= 1
  }

  let nextIndex = index + 1
  while (
    nextIndex < codePoints.length
    && EMOJI_SEQUENCE_EXTENDER_PATTERN.test(codePoints[nextIndex]!)
  ) {
    nextIndex += 1
  }

  return previousIndex >= 0
    && nextIndex < codePoints.length
    && EXTENDED_PICTOGRAPHIC_PATTERN.test(codePoints[previousIndex]!)
    && EXTENDED_PICTOGRAPHIC_PATTERN.test(codePoints[nextIndex]!)
}

function graphemeHasUnsafeInvisibleOrControl(grapheme: string): boolean {
  if (UNSAFE_INVISIBLE_OR_CONTROL_PATTERN.test(grapheme)) return true

  const codePoints = [...grapheme]
  return codePoints.some((codePoint, index) => (
    codePoint === ZERO_WIDTH_JOINER && !isEmojiZwjAt(codePoints, index)
  ))
}

function countCodePointsUpTo(text: string, stopAfter: number): number {
  let count = 0
  for (const _codePoint of text) {
    count += 1
    if (count >= stopAfter) return count
  }
  return count
}

export function validateFancyText(text: string): FancyTextValidation {
  const utf16Units = text.length
  const codePoints = countCodePointsUpTo(text, MAX_FANCY_TEXT_CODE_POINTS + 1)

  if (codePoints > MAX_FANCY_TEXT_CODE_POINTS) {
    return {
      status: 'error',
      error: 'code_point_limit',
      counts: { graphemes: 0, codePoints, utf16Units },
    }
  }
  const segments = segmentFancyText(text)
  const graphemes = segments.length
  const counts = { graphemes, codePoints, utf16Units }
  if (graphemes > MAX_FANCY_TEXT_GRAPHEMES) {
    return { status: 'error', error: 'grapheme_limit', counts }
  }
  return {
    status: 'success',
    counts,
    hasUnsafeControls: segments.some(graphemeHasUnsafeInvisibleOrControl),
  }
}

function mapAsciiCharacter(character: string, mapping: StyleMapping): string {
  const exception = mapping.exceptions?.[character]
  if (exception !== undefined) return String.fromCodePoint(exception)

  const codePoint = character.codePointAt(0)!
  if (codePoint >= 0x41 && codePoint <= 0x5a) {
    return String.fromCodePoint(mapping.upperStart + codePoint - 0x41)
  }
  if (codePoint >= 0x61 && codePoint <= 0x7a) {
    return String.fromCodePoint(mapping.lowerStart + codePoint - 0x61)
  }
  if (mapping.digitStart !== undefined && codePoint >= 0x30 && codePoint <= 0x39) {
    return String.fromCodePoint(mapping.digitStart + codePoint - 0x30)
  }
  return character
}

export function styleFancyText(text: string, style: FancyTextStyle): string {
  const mapping = STYLE_MAPPINGS[style]
  return segmentFancyText(text)
    .map((grapheme) => (/^[A-Za-z0-9]$/u.test(grapheme) ? mapAsciiCharacter(grapheme, mapping) : grapheme))
    .join('')
}

export function buildFancyTextResults(text: string): Record<FancyTextStyle, string> {
  return Object.fromEntries(
    FANCY_TEXT_STYLE_IDS.map((style) => [style, styleFancyText(text, style)]),
  ) as Record<FancyTextStyle, string>
}
